import type { FilterOperator } from './registry'

/**
 * OP_REGISTRY — the ONE source of op-string → UI detail mapping.
 * Every op-string that can appear in a type spec's filter_operators
 * MUST have an entry here. Covers text, number, select, checkbox, date,
 * reference families.
 *
 * resolveOperators(ops) maps the spec's string list to FilterOperator[]
 * objects the filter builder renders. No per-widget operator arrays exist;
 * every kind sources its operator list from the type spec via this registry.
 */
export const OP_REGISTRY: Record<string, { label: string; needsValue: boolean }> = {
  contains:    { label: 'contains',      needsValue: true  },
  eq:          { label: 'is',            needsValue: true  },
  neq:         { label: 'is not',        needsValue: true  },
  gt:          { label: '>',             needsValue: true  },
  lt:          { label: '<',             needsValue: true  },
  gte:         { label: '≥',             needsValue: true  },
  lte:         { label: '≤',             needsValue: true  },
  empty:       { label: 'is empty',      needsValue: false },
  'non-empty': { label: 'is not empty',  needsValue: false },
}

/**
 * Map an ordered op-string list (from attr.filter_operators on the wire)
 * to FilterOperator[] objects for the filter builder UI.
 * Unknown op-strings are silently dropped (forward-compat: new server ops
 * are invisible until this map is extended, rather than crashing).
 */
export function resolveOperators(ops: string[]): FilterOperator[] {
  const out: FilterOperator[] = []
  for (const op of ops) {
    const entry = OP_REGISTRY[op]
    if (entry) out.push({ op: op as FilterOperator['op'], ...entry })
  }
  return out
}
